const AbstractRepository = require('@/lib/Repositories/AbstractRepository');
const DataStoreNames = require('@/lib/DataStores/DataStoreNames');
const PrintDriverRefs = require('@/lib/Printing/PrintDriverRefs');
const Template = require('./Template');
const { basicHydrate } = require('@/lib/Hydration/basicHydrator');

/**
 * Repository for custom receipt templates stored in IndexedDB, originally provided by the API when syncing.
 * There may not be a template available, in which case the default template should be used.
 */
class TemplateRepository extends AbstractRepository {
    /**
     * @param {DataStore} dataStore
     * @param {Hydrator} hydrator
     * @param {SyncStateManager} syncStateManager
     * @param templateResource
     */
    constructor(
        dataStore,
        hydrator,
        syncStateManager,
        templateResource
    ) {
        super(dataStore, hydrator, syncStateManager);

        this.templateResource = templateResource;

        this.templateNamesToDownload = [
            // These are the only templates stored remotely. Others are part of the ProPoint code.
            'orders.epos.receipt',
            'orders.epos.ticket',
            'orders.epos.ticket-souvenir',
            'orders.epos.group-ticket',
            'orders.epos.group-ticket-souvenir',
        ];
    }

    getEntityClass() {
        return Template;
    }

    getEntityName() {
        return 'Template';
    }

    getTableName() {
        return DataStoreNames.TEMPLATES;
    }

    getResource() {
        return this.templateResource;
    }

    /**
     * Returns a single template by template name and print driver ref.
     *
     * @param {string} name
     * @param {string} printDriverRef
     *
     * @returns {Promise<Template>}
     */
    find(name, printDriverRef) {
        return this.dataStore
            .getStore(this.getTableName())
            .where({ name, printDriverRef })
            .toArray()
            .then((templates) => {
                if (templates.length < 1) {
                    // Throwing an error rejects the promise.
                    throw new Error(`${this.getEntityName()} ${name} for print driver ${printDriverRef} was not found.`);
                }

                return basicHydrate(templates[0], new (this.getEntityClass())());
            });
    }

    /**
     * @protected
     *
     * The templates endpoint works differently from other resources.
     * We need to make separate requests for each template/printDriver and each response is a string not an object.
     *
     * @return {Promise<Array>}
     */
    loadFromApi() {
        const promises = [];
        const templates = [];

        let printDriversToDownload = [
            // Only download the "print" templates because that's all we have now.
            PrintDriverRefs.PRINT,
        ];

        this.templateNamesToDownload.forEach((templateName) => {
            printDriversToDownload.forEach((printDriverRef) => {
                promises.push(
                    this.loadTemplateFromApi(templateName, printDriverRef)
                        .then((template) => {
                            templates.push(template);
                        })
                        .catch(() => {
                            // It's okay if no custom template exists.
                            // Printing will use the default bundled template instead.
                        })
                );
            });
        });

        return Promise.all(promises)
            .then(() => {
                return templates;
            });
    }

    /**
     * @param {string} templateName
     * @param {string} printDriverRef
     *
     * @return Promise<Template>
     */
    loadTemplateFromApi(templateName, printDriverRef) {
        return new Promise((resolve, reject) => {
            this.getResource().query(
                {
                    printMethod: printDriverRef,
                    templateName,
                },
                (response) => {
                    resolve(
                        this.hydrator.hydrate(
                            {
                                ...response,
                                name: templateName,
                                printDriverRef,
                            },
                            new Template()
                        )
                    );
                },
                reject
            );
        });
    }
}

module.exports = TemplateRepository;
